import { useEffect } from "react";
import { getMetaData } from "../../utils/Constant";

export default function FileUploadSecurity() {
  // Set title and metadata
  useEffect(() => {
    const metaData = getMetaData("file-upload-security-meta-data");

    document.title = metaData.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = metaData.description; // Use .content instead of setAttribute
  }, []);

  return (
    <div className="container mt-5 mb-5">
      <article className="bg-white rounded-4 shadow-lg border-0 overflow-hidden">
        {/* Header Section */}
        <header
          className="text-center py-5 px-3 position-relative"
          style={{
            background: "linear-gradient(135deg, #2c3e50 0%, #3498db 100%)",
            color: "#fff",
          }}
        >
          <div className="position-absolute top-0 start-0 w-100 h-100 opacity-10">
            <div
              className="position-absolute"
              style={{ top: "20%", left: "10%" }}
            >
              🔒
            </div>
            <div
              className="position-absolute"
              style={{ top: "60%", right: "15%" }}
            >
              🛡️
            </div>
            <div
              className="position-absolute"
              style={{ bottom: "30%", left: "20%" }}
            >
              ⚡
            </div>
          </div>
          <h1 className="display-5 fw-bold mb-3 position-relative">
            The Complete Guide to File Upload Security
          </h1>
          <p className="lead mb-0 position-relative fs-4">
            Best Practices for Developers to Secure File Uploads
          </p>
          <div className="mt-4 position-relative">
            <span className="badge bg-light text-dark fs-6 me-2 p-2">
              Security
            </span>
            <span className="badge bg-light text-dark fs-6 me-2 p-2">
              Best Practices
            </span>
            <span className="badge bg-light text-dark fs-6 p-2">
              Web Development
            </span>
          </div>
        </header>

        {/* Content Section */}
        <section className="p-4 p-md-5">
          {/* Introduction */}
          <div
            className="alert alert-warning border-0 rounded-3 shadow-sm"
            role="alert"
          >
            <div className="d-flex">
              <div className="me-3 fs-4">⚠️</div>
              <div>
                <p className="fs-5 lh-lg mb-0">
                  File uploads are one of the most common attack vectors in web
                  applications. Even a small vulnerability can expose your
                  system to severe risks. This guide explores essential security
                  practices every developer should follow.
                </p>
              </div>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="card border-0 shadow-sm mb-5">
            <div className="card-header bg-dark text-white py-3">
              <h2 className="h4 mb-0 fw-bold">📋 Table of Contents</h2>
            </div>
            <div className="card-body p-4">
              <div className="row">
                <div className="col-md-6">
                  <ul className="list-unstyled">
                    {[
                      "Why File Upload Security Matters",
                      "Client-Side vs Server-Side Validation",
                      "Multi-Layered Security Approach",
                      "File Type Verification",
                    ].map((item, index) => (
                      <li key={index} className="mb-2">
                        <a
                          href={`#section-${index + 1}`}
                          className="text-decoration-none text-dark hover-text-primary"
                        >
                          <span className="text-secondary me-2">
                            {index + 1}.
                          </span>
                          {item}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul className="list-unstyled">
                    {[
                      "Common Attack Vectors & Defenses",
                      "Implementation Checklist",
                      "Security Best Practices",
                      "Conclusion",
                    ].map((item, index) => (
                      <li key={index} className="mb-2">
                        <a
                          href={`#section-${index + 5}`}
                          className="text-decoration-none text-dark hover-text-primary"
                        >
                          <span className="text-secondary me-2">
                            {index + 5}.
                          </span>
                          {item}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Section 1 */}
          <div id="section-1" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-dark border-bottom pb-2">
              <span
                className="bg-dark text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                1
              </span>
              Why File Upload Security Matters
            </h2>
            <div className="row g-4">
              <div className="col-md-6">
                <div className="d-flex align-items-start p-3 border rounded-3 bg-light h-100">
                  <span className="text-danger fs-2 me-3">🔥</span>
                  <div>
                    <h5 className="fw-bold mb-1 text-dark">
                      Critical Attack Vector
                    </h5>
                    <p className="text-secondary mb-0">
                      File uploads are among the most exploited features in web
                      applications
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="d-flex align-items-start p-3 border rounded-3 bg-light h-100">
                  <span className="text-warning fs-2 me-3">💥</span>
                  <div>
                    <h5 className="fw-bold mb-1 text-dark">
                      Severe Consequences
                    </h5>
                    <p className="text-secondary mb-0">
                      Small vulnerabilities can lead to complete system
                      compromise
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2 */}
          <div id="section-2" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-dark border-bottom pb-2">
              <span
                className="bg-dark text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                2
              </span>
              Client-Side vs Server-Side Validation
            </h2>

            <div className="row g-4">
              <div className="col-lg-6">
                <div className="card border-danger h-100">
                  <div className="card-header bg-danger text-white py-3">
                    <h5 className="mb-0 fw-bold">🚫 Client-Side Validation</h5>
                  </div>
                  <div className="card-body">
                    <h6 className="fw-bold text-dark">
                      The Illusion of Security
                    </h6>
                    <ul className="text-secondary">
                      <li>
                        <strong>Purpose:</strong> Enhances user experience and
                        provides quick feedback
                      </li>
                      <li>
                        <strong>Limitations:</strong> Can easily be bypassed by
                        attackers
                      </li>
                      <li>
                        <strong>Never rely</strong> on it for real security
                      </li>
                      <li>
                        <strong>Typical Use:</strong> File type checks, size
                        warnings
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="card border-success h-100">
                  <div className="card-header bg-success text-white py-3">
                    <h5 className="mb-0 fw-bold">✅ Server-Side Validation</h5>
                  </div>
                  <div className="card-body">
                    <h6 className="fw-bold text-dark">The Real Protection</h6>
                    <ul className="text-secondary">
                      <li>
                        <strong>Purpose:</strong> Enforces actual security and
                        prevents attacks
                      </li>
                      <li>
                        <strong>Requirements:</strong> Comprehensive checks with
                        multiple layers
                      </li>
                      <li>
                        <strong>Implementation:</strong> Verify file content,
                        run malware scans
                      </li>
                      <li>
                        <strong>Essential</strong> for all production
                        applications
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 3 */}
          <div id="section-3" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-dark border-bottom pb-2">
              <span
                className="bg-dark text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                3
              </span>
              Multi-Layered Security Approach
            </h2>

            <div className="row g-4">
              {[
                {
                  icon: "🔍",
                  title: "Layer 1: File Type Verification",
                  items: [
                    "Never trust file extensions",
                    "Use magic numbers to check file headers",
                    "Confirm actual format beyond extensions",
                  ],
                },
                {
                  icon: "📏",
                  title: "Layer 2: Size & Dimension Limits",
                  items: [
                    "Prevent DoS attacks via large uploads",
                    "Restrict image dimensions",
                    "Check compression ratios for zip bombs",
                  ],
                },
                {
                  icon: "🛡️",
                  title: "Layer 3: Content Scanning",
                  items: [
                    "Integrate antivirus solutions",
                    "Detect hidden scripts or malicious code",
                    "Strip unsafe metadata before storage",
                  ],
                },
              ].map((layer, index) => (
                <div key={index} className="col-lg-4">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-header bg-light text-dark py-3">
                      <div className="d-flex align-items-center">
                        <span className="fs-2 me-2">{layer.icon}</span>
                        <h5 className="mb-0 fw-bold">{layer.title}</h5>
                      </div>
                    </div>
                    <div className="card-body">
                      <ul className="text-secondary">
                        {layer.items.map((item, itemIndex) => (
                          <li key={itemIndex} className="mb-2">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 4 */}
          <div id="section-4" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-dark border-bottom pb-2">
              <span
                className="bg-dark text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                4
              </span>
              File Type Verification Code Example
            </h2>
            <div className="card border-0 bg-light text-dark">
              <div className="card-header bg-secondary text-white py-2">
                <small className="fw-bold">Magic Number Verification</small>
              </div>
              <div className="card-body p-0">
                <pre className="p-3 mb-0 bg-dark text-light border-0">
                  <code className="text-black">
                    {`function verifyFileType(filePath) {
  const magicNumbers = {
    jpg: "\\xFF\\xD8\\xFF",
    png: "\\x89PNG\\r\\n\\x1a\\n",
    pdf: "%PDF-",
  };

  const handle = fopen(filePath, "r");
  const header = fread(handle, 8);
  fclose(handle);

  for (const [type, magic] of Object.entries(magicNumbers)) {
    if (header.startsWith(magic)) {
      return type;
    }
  }
  return false;
}`}
                  </code>
                </pre>
              </div>
            </div>
            <div className="alert alert-info mt-3 border-0">
              <strong>💡 Pro Tip:</strong> Always verify file types using magic
              numbers instead of file extensions. Attackers can easily rename
              malicious files to bypass extension checks.
            </div>
          </div>

          {/* Section 5 */}
          <div id="section-5" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-dark border-bottom pb-2">
              <span
                className="bg-dark text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                5
              </span>
              Common Attack Vectors & Defenses
            </h2>
            <div className="table-responsive">
              <table className="table table-hover align-middle shadow-sm">
                <thead className="table-dark">
                  <tr>
                    <th className="border-0">Attack Type</th>
                    <th className="border-0">Description</th>
                    <th className="border-0">Defense Strategy</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      attack: "Zip Bombs",
                      description:
                        "Tiny compressed files that expand to huge sizes",
                      defense: "Implement extraction ratio limits",
                    },
                    {
                      attack: "Path Traversal",
                      description:
                        "Using '../' patterns to access restricted directories",
                      defense:
                        "Sanitize file paths and isolate extraction directories",
                    },
                    {
                      attack: "File Type Spoofing",
                      description:
                        "Renaming executables to look like image files",
                      defense:
                        "Use magic number checks instead of file extensions",
                    },
                    {
                      attack: "Malware Upload",
                      description: "Uploading viruses or malicious scripts",
                      defense:
                        "Integrate antivirus scanning and content analysis",
                    },
                  ].map((row, index) => (
                    <tr key={index}>
                      <td className="fw-semibold text-danger">{row.attack}</td>
                      <td className="text-secondary">{row.description}</td>
                      <td className="text-success">{row.defense}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section 6 */}
          <div id="section-6" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-dark border-bottom pb-2">
              <span
                className="bg-dark text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                6
              </span>
              Implementation Checklist
            </h2>
            <div className="row g-3">
              {[
                "Verify file types using magic numbers",
                "Set strict file size and upload limits",
                "Scan all uploads with antivirus tools",
                "Store uploaded files outside the web root",
                "Use randomized filenames for storage",
                "Apply rate limiting on upload endpoints",
                "Sanitize all metadata before storage",
                "Test periodically with known malicious samples",
              ].map((item, index) => (
                <div key={index} className="col-md-6">
                  <div className="d-flex align-items-center p-3 border rounded-3 bg-light">
                    <div
                      className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                      style={{ width: "30px", height: "30px" }}
                    >
                      ✓
                    </div>
                    <span className="fw-medium text-dark">{item}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 7 */}
          <div id="section-7" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-dark border-bottom pb-2">
              <span
                className="bg-dark text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                7
              </span>
              Security Best Practices
            </h2>
            <div className="alert alert-danger border-0 rounded-3" role="alert">
              <div className="row g-3">
                {[
                  "Always treat uploaded files as potentially harmful",
                  "Implement multiple layers of validation",
                  "Never trust client-side validation alone",
                  "Regularly update your security measures",
                  "Conduct periodic security audits",
                  "Keep dependencies and libraries updated",
                ].map((practice, index) => (
                  <div
                    key={index}
                    className="col-md-6 d-flex align-items-start"
                  >
                    <span className="text-danger me-2 fw-bold">🛡️</span>
                    <span className="text-dark">{practice}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Conclusion */}
          <div id="section-8" className="border-top pt-4 mt-5">
            <h2 className="h3 fw-bold mb-4 text-dark">
              <span
                className="bg-dark text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                8
              </span>
              Conclusion
            </h2>
            <div className="card border-success border-2">
              <div className="card-body">
                <p className="fs-5 text-secondary mb-0">
                  File upload security is all about{" "}
                  <strong>layered defense</strong>. No single method can fully
                  protect your system — but combining multiple strategies
                  drastically reduces risks. Always treat uploaded files as
                  potentially harmful until proven safe, and keep testing your
                  setup regularly with controlled security tests.
                </p>
              </div>
            </div>
          </div>

          {/* Final CTA */}
          <div
            className="text-center mt-5 p-5 rounded-4 border"
            style={{ backgroundColor: "#f8f9fa" }}
          >
            <h2 className="h1 fw-bold mb-3 text-dark">
              Ready to Secure Your Uploads?
            </h2>
            <p className="fs-5 text-secondary mb-4">
              Implement these security practices to protect your application
              from file-based attacks.
            </p>
            <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
              <a
                href="#section-1"
                className="btn btn-dark btn-lg px-5 py-3 shadow hover-lift"
              >
                🔒 Review Security Guide
              </a>
              <a
                href="#section-6"
                className="btn btn-outline-dark btn-lg px-5 py-3"
              >
                📋 View Checklist
              </a>
            </div>
          </div>
        </section>
      </article>
    </div>
  );
}
